// Curated Le Bon Shoppe catalog for the Catalog Connect prototype.
// All product data sourced from src/data/entity_db/product/*.ts (real Faire product
// entities under brand b_85a3118wke). Descriptions, prices, images, and gallery URLs
// are pulled at module load via getEntityByToken so this file stays a thin manifest.
//
// Buckets:
//  - HIGH_POTENTIAL (5)         — hero/marketing products. Used in homepage banner
//                                  tile grids, Add-from-URL modal, SPE typeahead
//                                  suggestions, popup banner thumbnails, post-publish
//                                  "you may also want" suggestions.
//  - OTHER_FOUND (8)            — fills out the "Found on your website" table.
//  - LOW_CONFIDENCE (5)         — Tier 3 products hidden behind "Show more" footer.
//  - MISSING_OPTIONS (3)        — already published on Faire, missing some options.
//                                  Appears in BOTH "All products" and "Found on your
//                                  website" buckets.
//  - ALREADY_IN_CATALOG (17)    — rest of the All-products bucket (not on website list).
//
// Convenience exports:
//  - ALL_PRODUCTS (20)            = ALREADY_IN_CATALOG ∪ MISSING_OPTIONS
//  - FOUND_ON_WEBSITE_POOL (16)   = HIGH_POTENTIAL ∪ OTHER_FOUND ∪ MISSING_OPTIONS
//
// NOTE: ALL_PRODUCTS bucket exists but ProductsPageView's "All products" tab is
// currently a placeholder UI — wire-up TODO.
//
// Adapters:
//  - toRTLProduct(p, opts?) → RTLProduct shape used by ReadyToList/CCRTLTableSection.
//  - toMockProduct(p)       → MockProduct shape used by RestingTypeaheadSuggestionsPage.

import { getEntityByToken } from "../../data/get_entity";
import type { FaireProductEntity } from "@faire/playground-entity-db/entity_types/product";

// ── Brand metadata ──

const BRAND_TOKEN = "b_85a3118wke";

const BRAND = getEntityByToken(BRAND_TOKEN, "brand");

export const LE_BON_SHOPPE_BRAND = {
  token: BRAND_TOKEN,
  name: BRAND?.name ?? "Le Bon Shoppe",
  avatarUrl:
    BRAND?.avatarUrl?.includes("cdn.faire.com") &&
    !BRAND.avatarUrl.includes("?")
      ? `${BRAND.avatarUrl}?width=80`
      : BRAND?.avatarUrl,
};
import type { MockProduct } from "../RestingTypeaheadSuggestionsPage/mockProducts";
import type { RTLOptionDetail, RTLProduct } from "../ReadyToList/mockData";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface LeBonShoppeProduct {
  id: string;
  name: string;
  sku: string;
  priceDollars: number;
  msrpDollars: number;
  description: string;
  primaryImage: string;
  thumbnail: string;
  galleryImages: string[];
  category: string;
  goodFitReason?: string;
  /** Names of options already on Faire — used by MISSING_OPTIONS bucket. */
  existingOptions?: string[];
  /** Detailed already-on-Faire options — drives the expanded preview row. */
  existingOptionDetails?: RTLOptionDetail[];
  /** Detailed missing options — drives the expanded preview row. */
  optionDetails?: RTLOptionDetail[];
  /** Top-line option summary (e.g. "2 options"). */
  optionsLabel?: string;
  /** Plain option names (e.g. ["Black", "White"]). */
  optionNames?: string[];
}

// ─── Manifest entries ────────────────────────────────────────────────────────

interface ManifestEntry {
  token: string;
  sku: string;
  goodFitReason?: string;
  existingOptions?: string[];
  existingOptionDetails?: RTLOptionDetail[];
  optionDetails?: RTLOptionDetail[];
  optionsLabel?: string;
  optionNames?: string[];
}

// 5 hero/marketing products — diverse mix of jacket, dress, cardigan, pants, socks.
const HIGH_POTENTIAL_MANIFEST: ManifestEntry[] = [
  {
    token: "p_ybnjdgeuqa",
    sku: "LBS-BLAIR-WX",
    goodFitReason:
      "Outerwear is the #1 reorder category for apparel retailers on Faire — waxed cotton jackets at this price point convert above category average.",
  },
  {
    token: "p_fqgsrbtc36",
    sku: "LBS-CAMILLE",
    goodFitReason:
      "Cotton-hemp dresses in your price range are searched 3x more often than the category baseline — strong fit for boutique buyers.",
  },
  {
    token: "p_4l7gox0pay",
    sku: "LBS-CLOUD",
    goodFitReason:
      "Le Bon Shoppe socks are a Faire bestseller — adding the Cloud variant fills out a category that retailers consistently reorder.",
  },
  {
    token: "p_e6nbry9s6y",
    sku: "LBS-LEILA-CT",
    goodFitReason:
      "Lightweight knit cardigans are trending up 40% with Faire retailers this season — well-positioned to capture that demand.",
  },
  {
    token: "p_v7wexa7er5",
    sku: "LBS-WORK-PT",
    goodFitReason:
      "Cotton work pants are a top-converting subcategory for boutique apparel buyers on Faire.",
  },
];

// 8 other "Found on your website" products.
const OTHER_FOUND_MANIFEST: ManifestEntry[] = [
  { token: "p_ugmvyztm6r", sku: "LBS-OWEN" },
  { token: "p_3u3anvm8g8", sku: "LBS-SAILOR" },
  { token: "p_4vapkv9ftw", sku: "LBS-FT-BAL" },
  { token: "p_pya9runhnt", sku: "LBS-CLUB-CD" },
  { token: "p_zwvyss6ych", sku: "LBS-CITY-SH" },
  { token: "p_8cema7cqch", sku: "LBS-GOTO-TEE" },
  { token: "p_mxvhpenabt", sku: "LBS-LILY-CMI" },
  { token: "p_vhpnmrwakt", sku: "LBS-HAPPY-BG" },
];

// 5 Tier-3 low-confidence products (mostly socks).
const LOW_CONFIDENCE_MANIFEST: ManifestEntry[] = [
  { token: "p_pebt5pk5bj", sku: "LBS-COTTAGE" },
  { token: "p_uc4zs2qxka", sku: "LBS-PETITE-RF" },
  { token: "p_ved47j3xct", sku: "LBS-EYELET" },
  { token: "p_wgvhps7fwj", sku: "LBS-SWING" },
  { token: "p_aadnqwxg3g", sku: "LBS-HER-GLW" },
];

// 3 published-with-missing-options products. Appear in both All products and
// Found on your website tables. Each lists the colors/options already on Faire
// so the prototype can render "Published — Missing N options".
const MISSING_OPTIONS_MANIFEST: ManifestEntry[] = [
  {
    token: "p_7776n8vgpr",
    sku: "LBS-BFRD",
    optionsLabel: "3 options",
    optionNames: ["Black/White", "Navy/Cream", "Sage/Cream"],
    optionDetails: [
      {
        name: "Black/White",
        sku: "LBS-BFRD-BW",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/9089e9b7fb037d263c1764049dda7f09e4aaf31d9c79002d37c3ced69cff26b3.jpeg",
      },
      {
        name: "Navy/Cream",
        sku: "LBS-BFRD-NC",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/92fa7d36472091984ff48695f93499f96cca5ea00df6ffad913439fe2f16cc99.jpeg",
      },
      {
        name: "Sage/Cream",
        sku: "LBS-BFRD-SC",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/8075bc079df5945c131de590534bdb8a50a0723969e0b189adaa21f244e97f0b.jpeg",
      },
    ],
    existingOptions: ["Cream/Black", "Charcoal"],
    existingOptionDetails: [
      {
        name: "Cream/Black",
        sku: "LBS-BFRD-CB",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/2469d2f32c25344b0a1f1082ba2125d105b65aadebf0e89a9754d8cbeb76ac51.jpeg",
      },
      {
        name: "Charcoal",
        sku: "LBS-BFRD-CH",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/f6d69bda7e0715240f2fb8b2021282c1f9f1ab4661dd0166c30b9942099856c0.jpeg",
      },
    ],
    goodFitReason:
      "Already proven with Faire retailers — you're missing 3 colors that your existing buyers are likely searching for.",
  },
  {
    token: "p_cn7igzpn01",
    sku: "LBS-GFRD",
    optionsLabel: "2 options",
    optionNames: ["Mustard", "Lavender"],
    optionDetails: [
      {
        name: "Mustard",
        sku: "LBS-GFRD-MU",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/9089e9b7fb037d263c1764049dda7f09e4aaf31d9c79002d37c3ced69cff26b3.jpeg",
      },
      {
        name: "Lavender",
        sku: "LBS-GFRD-LV",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/92fa7d36472091984ff48695f93499f96cca5ea00df6ffad913439fe2f16cc99.jpeg",
      },
    ],
    existingOptions: ["Cream", "Black", "Olive"],
    existingOptionDetails: [
      {
        name: "Cream",
        sku: "LBS-GFRD-CR",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/2469d2f32c25344b0a1f1082ba2125d105b65aadebf0e89a9754d8cbeb76ac51.jpeg",
      },
      {
        name: "Black",
        sku: "LBS-GFRD-BK",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/f6d69bda7e0715240f2fb8b2021282c1f9f1ab4661dd0166c30b9942099856c0.jpeg",
      },
      {
        name: "Olive",
        sku: "LBS-GFRD-OL",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/8075bc079df5945c131de590534bdb8a50a0723969e0b189adaa21f244e97f0b.jpeg",
      },
    ],
    goodFitReason:
      "You're already published with 3 colors — adding the missing 2 closes the gap your existing buyers are looking for.",
  },
  {
    token: "p_ejubgkebwj",
    sku: "LBS-HER-MC",
    optionsLabel: "2 options",
    optionNames: ["Forest", "Rust"],
    optionDetails: [
      {
        name: "Forest",
        sku: "LBS-HER-MC-FR",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/9089e9b7fb037d263c1764049dda7f09e4aaf31d9c79002d37c3ced69cff26b3.jpeg",
      },
      {
        name: "Rust",
        sku: "LBS-HER-MC-RT",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/92fa7d36472091984ff48695f93499f96cca5ea00df6ffad913439fe2f16cc99.jpeg",
      },
    ],
    existingOptions: ["Ivory", "Black", "Sky"],
    existingOptionDetails: [
      {
        name: "Ivory",
        sku: "LBS-HER-MC-IV",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/2469d2f32c25344b0a1f1082ba2125d105b65aadebf0e89a9754d8cbeb76ac51.jpeg",
      },
      {
        name: "Black",
        sku: "LBS-HER-MC-BK",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/f6d69bda7e0715240f2fb8b2021282c1f9f1ab4661dd0166c30b9942099856c0.jpeg",
      },
      {
        name: "Sky",
        sku: "LBS-HER-MC-SK",
        price: "$6.00",
        image:
          "https://cdn.faire.com/fastly/8075bc079df5945c131de590534bdb8a50a0723969e0b189adaa21f244e97f0b.jpeg",
      },
    ],
    goodFitReason:
      "Faire data shows retailers buy this style across 5+ colors on average — adding your remaining 2 options recaptures lost reorders.",
  },
];

// 17 already-in-catalog products (the rest of the All-products bucket, excluding
// the 3 missing-options entries which also appear in this tab).
const ALREADY_IN_CATALOG_MANIFEST: ManifestEntry[] = [
  { token: "p_3mf3f973m7", sku: "LBS-ALDEN" },
  { token: "p_3ts3qmvnuh", sku: "LBS-PRCH-A" },
  { token: "p_4abqq5vrzg", sku: "LBS-WD-WLY" },
  { token: "p_4i33osnkcm", sku: "LBS-SNK" },
  { token: "p_4wv6v7e7wy", sku: "LBS-DARLG" },
  { token: "p_5yqs76q496", sku: "LBS-PT-BAB" },
  { token: "p_5zppv4c8pw", sku: "LBS-RF-HG" },
  { token: "p_891nv0x7f7", sku: "LBS-EASE" },
  { token: "p_8kwp6tdehy", sku: "LBS-VFL" },
  { token: "p_9p4puhyfeb", sku: "LBS-BARE" },
  { token: "p_atsvlh26mt", sku: "LBS-OVB" },
  { token: "p_ba9yrq92rv", sku: "LBS-LIAM" },
  { token: "p_bd8s9ykvwq", sku: "LBS-FT-CR" },
  { token: "p_e8p87pyfar", sku: "LBS-CALI" },
  { token: "p_f3c75z6vje", sku: "LBS-DANA" },
  { token: "p_f538p9j4m3", sku: "LBS-MARS" },
  { token: "p_qkpaf7z22z", sku: "LBS-PAPA" },
];

// ─── Resolution ──────────────────────────────────────────────────────────────

const APPAREL_CATEGORY = "Apparel & Accessories";
const FALLBACK_DESCRIPTION_LENGTH = 220;

const truncateDescription = (raw: string): string => {
  // Le Bon Shoppe entity descriptions consistently start with a "Why we made
  // this?" heading (sometimes in stylized Unicode bold). Strip everything up
  // through the first question mark, then collapse whitespace and truncate to
  // a single readable paragraph.
  const firstQ = raw.indexOf("?");
  const body = firstQ >= 0 ? raw.slice(firstQ + 1) : raw;
  const cleaned = body.replace(/[\s\n]+/g, " ").trim();
  if (cleaned.length <= FALLBACK_DESCRIPTION_LENGTH) return cleaned;
  const slice = cleaned.slice(0, FALLBACK_DESCRIPTION_LENGTH);
  const lastSpace = slice.lastIndexOf(" ");
  return `${lastSpace > 0 ? slice.slice(0, lastSpace) : slice}…`;
};

const resolveEntity = (token: string): FaireProductEntity => {
  const entity = getEntityByToken(token, "product");
  if (!entity) {
    throw new Error(
      `Le Bon Shoppe product entity ${token} not found in entity_db.`
    );
  }
  return entity;
};

/** Faire CDN URLs are full-resolution by default. Append `?width=N` so the
 * Fastly Image Optimizer returns a properly sized image. Skips non-Faire URLs
 * (Unsplash etc.) and URLs that already carry query params. */
const cdnSized = (url: string, width: number): string => {
  if (!url) return url;
  if (!url.includes("cdn.faire.com")) return url;
  if (url.includes("?")) return url;
  return `${url}?width=${width}`;
};

const sizeOptionDetails = (
  details: RTLOptionDetail[] | undefined
): RTLOptionDetail[] | undefined =>
  details?.map((d) => ({ ...d, image: cdnSized(d.image, 160) }));

const fromManifest = (entry: ManifestEntry): LeBonShoppeProduct => {
  const e = resolveEntity(entry.token);
  const gallery = e.galleryImageUrls ?? [];
  return {
    id: entry.token,
    name: e.name.replace(/\s*\(Wholesale[^)]*\)\s*/i, "").trim(),
    sku: entry.sku,
    priceDollars: e.priceDollars,
    msrpDollars: e.msrpDollars ?? e.priceDollars * 2,
    description: truncateDescription(e.description),
    primaryImage: cdnSized(e.imageUrl, 800),
    thumbnail: cdnSized(e.imageUrl, 160),
    galleryImages: gallery.map((u) => cdnSized(u, 800)),
    category: APPAREL_CATEGORY,
    goodFitReason: entry.goodFitReason,
    existingOptions: entry.existingOptions,
    existingOptionDetails: sizeOptionDetails(entry.existingOptionDetails),
    optionDetails: sizeOptionDetails(entry.optionDetails),
    optionsLabel: entry.optionsLabel,
    optionNames: entry.optionNames,
  };
};

// ─── Bucket exports ──────────────────────────────────────────────────────────

export const HIGH_POTENTIAL: LeBonShoppeProduct[] =
  HIGH_POTENTIAL_MANIFEST.map(fromManifest);

export const OTHER_FOUND: LeBonShoppeProduct[] =
  OTHER_FOUND_MANIFEST.map(fromManifest);

export const LOW_CONFIDENCE: LeBonShoppeProduct[] =
  LOW_CONFIDENCE_MANIFEST.map(fromManifest);

export const MISSING_OPTIONS: LeBonShoppeProduct[] =
  MISSING_OPTIONS_MANIFEST.map(fromManifest);

export const ALREADY_IN_CATALOG: LeBonShoppeProduct[] =
  ALREADY_IN_CATALOG_MANIFEST.map(fromManifest);

export const ALL_PRODUCTS: LeBonShoppeProduct[] = [
  ...ALREADY_IN_CATALOG,
  ...MISSING_OPTIONS,
];

export const FOUND_ON_WEBSITE_POOL: LeBonShoppeProduct[] = [
  ...HIGH_POTENTIAL,
  ...OTHER_FOUND,
  ...MISSING_OPTIONS,
];

// ─── Mock "discovered on website" dates ──────────────────────────────────────
// HIGH_POTENTIAL gets the most recent dates so they sort to the top under
// "Newest". LOW_CONFIDENCE gets the oldest. Dates are spaced 1 day apart
// within each bucket.
const dateString = (daysAgo: number): string => {
  const d = new Date("2026-04-29T12:00:00Z");
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
};

const PRODUCT_DATE_BY_ID = new Map<string, string>();
[...HIGH_POTENTIAL, ...OTHER_FOUND, ...MISSING_OPTIONS, ...LOW_CONFIDENCE].forEach(
  (p, i) => {
    // i=0 is the newest (today); each subsequent entry is one day older.
    PRODUCT_DATE_BY_ID.set(p.id, dateString(i));
  }
);
export const getDateAddedForProduct = (id: string): string | undefined =>
  PRODUCT_DATE_BY_ID.get(id);

// ─── Adapters ────────────────────────────────────────────────────────────────

const formatUsd = (n: number): string =>
  `$${n.toFixed(2).replace(/\.00$/, ".00")}`;

const wholesaleOf = (priceDollars: number): string => formatUsd(priceDollars);
const retailOf = (msrpDollars: number): string => formatUsd(msrpDollars);

interface ToRTLProductOpts {
  /**
   * Override status string. When undefined, the adapter derives status from
   * existingOptions: "Published — Missing N option(s)" or "Not on Faire".
   */
  status?: string;
  /** Override the "good fit" pinned reason for this row. */
  goodFitReason?: string;
}

export const toRTLProduct = (
  p: LeBonShoppeProduct,
  opts: ToRTLProductOpts = {}
): RTLProduct => {
  const extraImageCount = Math.min(p.galleryImages.length, 4);
  const missingOptionCount = p.optionDetails?.length ?? 0;
  const status =
    opts.status ??
    (p.existingOptions
      ? `Published — Missing ${missingOptionCount} option${missingOptionCount === 1 ? "" : "s"}`
      : "Not on Faire");
  return {
    id: p.id,
    name: p.name,
    sku: p.sku,
    options: p.optionsLabel,
    optionNames: p.optionNames,
    optionDetails: p.optionDetails,
    status,
    dateAdded: PRODUCT_DATE_BY_ID.get(p.id),
    image: p.primaryImage,
    extraImageCount,
    price: retailOf(p.msrpDollars),
    retailPrice: retailOf(p.msrpDollars),
    wholesalePrice: wholesaleOf(p.priceDollars),
    description: p.description,
    category: p.category,
    sellingMethod: "Item",
    existingOptions: p.existingOptions,
    existingOptionDetails: p.existingOptionDetails,
    goodFitReason: opts.goodFitReason ?? p.goodFitReason,
  };
};

export const toMockProduct = (p: LeBonShoppeProduct): MockProduct => {
  // Stable pseudo-random retailer search count derived from the token so each
  // product gets a believable but consistent number on every load.
  const seed = p.id
    .split("")
    .reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 7);
  const retailerSearchCount = 12 + (seed % 37); // 12–48
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    thumbnail: p.thumbnail,
    primaryImage: p.primaryImage,
    secondaryImages: p.galleryImages.slice(0, 4),
    highValue: !!p.goodFitReason,
    retailerSearchCount,
    sku: p.sku,
  };
};
